// frontend/src/pages/builder/formatSettings.js
// Phase 3 step 7b-2c: the builder's Format settings -- the chart's title and
// how its numbers are written (decision D10). Pure and unit-tested.
//
// The title follows the fields ("Average Salary by Department") until the
// user types one, the way Power BI's default title works; clearing it goes
// back to the automatic one. The presentation sent with the chart is the
// same canonical form the server stores (backend app/semantic/presentation.py):
// only what differs from the defaults, the currency only with the currency
// format, and value labels only for chart types that draw them.
import { VALUE_LABEL_TYPES } from '../../charts/echartsOption';
import { CURRENCIES, MAX_DECIMALS, MEASURE_FORMATS, makeFormatter } from '../../charts/format';
import { measureLabel } from './builderSpec';

export const NUMBER_FORMAT_OPTIONS = [
  { id: 'auto', label: 'Auto' },
  { id: 'full', label: 'Full number' },
  { id: 'compact', label: 'Compact' },
  { id: 'percent', label: 'Percent' },
  { id: 'currency', label: 'Currency' },
];
export const CURRENCY_OPTIONS = CURRENCIES;
export const DECIMAL_OPTIONS = ['auto', ...Array.from({ length: MAX_DECIMALS + 1 }, (_, i) => String(i))];
export const MAX_TITLE = 120;

export const emptyFormat = () => ({ title: '', number_format: 'auto', currency: 'USD', decimals: null, value_labels: false });

/** What the user typed, kept to one line and a sensible length. */
export function cleanTitle(text) {
  return String(text || '').replace(/[\u0000-\u001f\u007f]+/g, ' ').slice(0, MAX_TITLE);
}

/** The heading shown on the chart: the typed title, or the automatic one. */
export function chartTitle(format, autoTitle) {
  const typed = cleanTitle(format && format.title).trim();
  return typed || autoTitle;
}

export const valueLabelsApply = (type) => VALUE_LABEL_TYPES.includes(type);

/**
 * The presentation for this chart type, in the server's canonical form; null
 * when all defaults. `columns` (7d-2) is a table's column order and widths
 * from tableColumns.columnsPresentation, kept for the table and (7e-3, widths
 * only) the matrix.
 */
export function toPresentation(format, type, columns = null) {
  const f = { ...emptyFormat(), ...(format || {}) };
  const out = {};
  if (f.number_format !== 'auto') out.number_format = f.number_format;
  if (f.number_format === 'currency') out.currency = CURRENCIES.includes(f.currency) ? f.currency : 'USD';
  if (Number.isInteger(f.decimals) && f.decimals >= 0 && f.decimals <= MAX_DECIMALS) out.decimals = f.decimals;
  if (f.value_labels === true && valueLabelsApply(type)) out.value_labels = true;
  if ((type === 'table' || type === 'matrix') && Array.isArray(columns) && columns.length) out.columns = columns;
  return Object.keys(out).length ? out : null;
}

const MEASURE_FORMAT_WORDS = { integer: 'a whole number', percent: 'a percent', currency: 'money' };

/**
 * Under "Auto" (13c-4), a saved measure or a calculated column with a format
 * set on the Model page is drawn in it; this names them so the builder knows
 * why one series reads $220K and the next 25%. Null when nothing applies
 * (no such measure, or the chart chose a format, which then wins everywhere).
 */
export function measureFormatNote(format, measures, fieldsByKey = {}) {
  if ((format && format.number_format) !== 'auto') return null;
  const parts = [];
  for (const m of measures || []) {
    const f = m && fieldsByKey[m.field];
    const fmt = f && f.format;
    const keeps = m.aggregation === 'metric' || ['sum', 'avg', 'min', 'max'].includes(m.aggregation);
    if (keeps && MEASURE_FORMATS.includes(fmt) && fmt !== 'number') parts.push(`${measureLabel(m, fieldsByKey)} as ${MEASURE_FORMAT_WORDS[fmt]}`);
  }
  if (!parts.length) return null;
  return `Auto shows ${parts.join(', ')}, as set on the Model page. Choosing a format here applies it to every value.`;
}

/** "12.9K on the axis, 12,873.5 in tooltips": what the chosen format does, shown under the control. */
export function formatExample(format) {
  const p = toPresentation({ ...format, value_labels: false }, 'bar');
  const sample = p && p.number_format === 'percent' ? 0.1234 : 12873.5;
  return `${makeFormatter(p, 'axis')(sample)} on the axis, ${makeFormatter(p, 'tooltip')(sample)} in tooltips`;
}
